import { useEffect, useRef } from 'react';
import { createLogger } from '@/lib/logger';
import { Stack, useRouter, usePathname, useSegments } from 'expo-router';
import { StyleSheet, Text, View, TouchableOpacity } from 'react-native';
import { Home } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';

const log = createLogger('_NOT_FOUND');

export default function NotFoundScreen() {
  const router = useRouter();
  const pathname = usePathname();
  const segments = useSegments();
  const hasRedirected = useRef(false);
  const { theme } = useTheme();

  useEffect(() => {
    log.info(`[NOT_FOUND] pathname=${pathname} segments=${JSON.stringify(segments)}`);

    if (!hasRedirected.current) {
      hasRedirected.current = true;
      const timeout = setTimeout(() => {
        log.info('[NOT_FOUND] Redirecting to /gate');
        router.replace('/gate');
      }, __DEV__ ? 2000 : 300);
      return () => clearTimeout(timeout);
    }
  }, [pathname, segments, router]);

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <Text style={[styles.title, { color: theme.colors.accent }]}>404</Text>
        <Text style={[styles.text, { color: theme.colors.textSecondary }]}>This screen doesn&apos;t exist.</Text>
        {__DEV__ && (
          <View style={[styles.devInfo, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
            <Text style={[styles.devLabel, { color: theme.colors.accent }]}>Pathname:</Text>
            <Text style={[styles.devValue, { color: theme.colors.text }]}>{pathname}</Text>
            <Text style={[styles.devLabel, { color: theme.colors.accent }]}>Segments:</Text>
            <Text style={[styles.devValue, { color: theme.colors.text }]}>{JSON.stringify(segments)}</Text>
          </View>
        )}
        <TouchableOpacity
          style={[styles.button, { backgroundColor: theme.colors.accent }]}
          onPress={() => router.replace('/gate')}>
          <Home size={20} color={theme.colors.accentForeground} />
          <Text style={[styles.buttonText, { color: theme.colors.accentForeground }]}>Go Home</Text>
        </TouchableOpacity>
        {__DEV__ && (
          <Text style={[styles.devText, { color: theme.colors.textTertiary }]}>Redirecting in 2 seconds...</Text>
        )}
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  title: {
    fontSize: 72,
    fontWeight: '900',
    marginBottom: 16,
  },
  text: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 32,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 2,
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '700',
  },
  devText: {
    fontSize: 12,
    marginTop: 24,
  },
  devInfo: {
    padding: 16,
    borderRadius: 2,
    borderWidth: 1,
    marginBottom: 24,
    alignSelf: 'stretch',
  },
  devLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginTop: 8,
    marginBottom: 4,
  },
  devValue: {
    fontSize: 12,
    fontFamily: 'monospace',
  },
});
